interface JWTPayload {
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decodes a JWT token without verification (client-side only)
 * Note: This is for expiration checking only, not security validation
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    // JWT has 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decode base64
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // If we can't read expiration, consider it expired
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
};

/**
 * Get the expiration time of a JWT token in seconds from now
 */
export const getTokenExpirationTime = (token: string): number | null => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp - now;
};

/**
 * Check if token will expire within the specified number of seconds
 */
export const willTokenExpireSoon = (token: string, withinSeconds: number): boolean => {
  const timeLeft = getTokenExpirationTime(token);
  if (timeLeft === null) {
    return true;
  }
  return timeLeft <= withinSeconds;
};

/**
 * Format time remaining as human readable string
 */
export const formatTimeRemaining = (seconds: number): string => {
  if (seconds <= 0) return 'expired';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};